import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { GROQCornerButton } from "@/sanity/lib/definitions";
import { stegaClean } from "@sanity/client/stega";
import { Slot } from "@radix-ui/react-slot";
import { CTAWrapper } from "@/components/global/utils/CTAWrapper";
import { iconMap } from "@/lib/iconMap";

const cornerButtonVariants = cva("corner-button", {
  variants: {
    type: {
      default: "corner-cta corner-button-default",
      dark: "corner-cta corner-button-dark",
      clearOutline: "corner-cta corner-clear-outline",
    },
    action: {
      link: "",
      popup: "",
      anchor: "",
      none: "",
    },
  },
  defaultVariants: {
    type: "default",
    action: "none",
  },
});

export interface CornerButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type">,
    VariantProps<typeof cornerButtonVariants> {
  asChild?: boolean;
  htmlType?: "button" | "submit" | "reset";
}

const CornerButton = React.forwardRef<HTMLButtonElement, CornerButtonProps>(
  ({ className, type, action, asChild = false, htmlType = "button", ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const children = props.children;
    return (
      <Comp
        className={cn(
          "group",
          cornerButtonVariants({ type, action }),
          className,
        )}
        data-type={type}
        data-action={action}
        ref={ref}
        type={htmlType}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
CornerButton.displayName = "CornerButton";

export interface CTACornerButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  data: GROQCornerButton;
}

const CTACornerButton = React.forwardRef<
  HTMLButtonElement,
  CTACornerButtonProps
>(({ className, data, type: _type, ...props }, ref) => {
  const cleanedData = stegaClean(data ?? {}) ?? null;

  const buttonType = (cleanedData?.type ?? "default") as "default" | "dark";

  const hasIcon = cleanedData?.cta?.icon ?? "none";
  const icon = iconMap[hasIcon as keyof typeof iconMap];

  const action = cleanedData?.cta?.action ?? null;

  return (
    <CTAWrapper
      cta={cleanedData?.cta}
      className={cn(
        "inline-block w-full min-w-[30rem] sm:w-auto relative z-10",
        className,
      )}
    >
      <CornerButton
        className="relative w-full group [clip-path:polygon(0_0,100%_0,100%_calc(100%-2rem),calc(100%-2rem)_100%,0_100%)]"
        type={buttonType}
        action={action ?? "none"}
        ref={ref}
        {...props}
      >
        <div className="corner-button-text relative">
          <div className="corner-button-text-initial transition-all duration-300">
            {data?.cta
              ? (data.cta.title ?? "Please add a title")
              : "Please select a CTA"}
          </div>
          <div className="corner-button-text-hover absolute top-[3rem] transition-all duration-300 pointer-events-none">
            {data?.cta
              ? (data.cta.title ?? "Please add a title")
              : "Please select a CTA"}
          </div>
        </div>

        {icon && (
          <>
            <div className="corner-button-divider h-[4rem]" />

            <div className="corner-button-icon relative [&_svg]:w-full overflow-clip">
              <div className="corner-button-icon-initial transition-all duration-300 w-[3rem] h-[3rem]">
                {icon}
              </div>
              <div className="corner-button-icon-hover absolute top-[2.25rem] -left-[2.25rem] transition-all duration-300 pointer-events-none w-[3rem] h-[3rem]">
                {icon}
              </div>
            </div>
          </>
        )}
      </CornerButton>
    </CTAWrapper>
  );
});
CTACornerButton.displayName = "CTACornerButton";

export { CornerButton, cornerButtonVariants, CTACornerButton };
